import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Clock, CheckCircle2, CalendarDays } from 'lucide-react'
import { supabase } from '@/config/supabase'
import { ROUTES } from '@/config/routes'
import useAuthStore from '@/stores/authStore'
import Button from '@/components/ui/Button'

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt12(time24) {
  if (!time24) return ''
  const [h, m] = time24.slice(0, 5).split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12  = h % 12 || 12
  return `${h12}:${String(m).padStart(2,'0')} ${ampm}`
}

function ymd(date) {
  return date.toISOString().split('T')[0]
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate()
}

function firstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay() // 0=Sun
}

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]
const DAY_LABELS = ['Su','Mo','Tu','We','Th','Fr','Sa']

// ── Mini calendar ─────────────────────────────────────────────────────────────

function Calendar({ year, month, availableDates, selectedDate, onSelectDate, onPrevMonth, onNextMonth }) {
  const today    = ymd(new Date())
  const numDays  = daysInMonth(year, month)
  const firstDay = firstDayOfMonth(year, month)

  // Build grid cells: nulls for leading empty, then 1..numDays
  const cells = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: numDays }, (_, i) => i + 1),
  ]

  return (
    <div className="bg-surface-raised border border-surface-border rounded-xl overflow-hidden">

      {/* Month nav */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
        <button
          type="button"
          onClick={onPrevMonth}
          className="p-1.5 rounded-lg text-ink-muted hover:text-ink-primary hover:bg-surface-overlay transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <p className="text-sm font-semibold text-ink-primary">
          {MONTH_NAMES[month]} {year}
        </p>
        <button
          type="button"
          onClick={onNextMonth}
          className="p-1.5 rounded-lg text-ink-muted hover:text-ink-primary hover:bg-surface-overlay transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 border-b border-surface-border">
        {DAY_LABELS.map((d) => (
          <div key={d} className="py-2 text-center text-[10px] font-semibold text-ink-muted uppercase tracking-widest">
            {d}
          </div>
        ))}
      </div>

      {/* Date cells */}
      <div className="grid grid-cols-7 p-2 gap-0.5">
        {cells.map((day, idx) => {
          if (!day) return <div key={'e' + idx} />

          const dateStr    = `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`
          const isPast     = dateStr < today
          const hasSlot    = availableDates.has(dateStr)
          const isSelected = selectedDate === dateStr

          return (
            <button
              key={dateStr}
              type="button"
              disabled={isPast || !hasSlot}
              onClick={() => onSelectDate(dateStr)}
              className={`
                relative aspect-square flex flex-col items-center justify-center rounded-lg text-sm
                transition-colors
                ${isSelected
                  ? 'bg-siglat-teal text-surface-base font-bold'
                  : hasSlot && !isPast
                    ? 'bg-surface-overlay text-ink-primary hover:bg-siglat-teal/20 hover:text-siglat-teal font-medium'
                    : 'text-ink-disabled cursor-not-allowed'
                }
              `}
            >
              {day}
              {/* Green dot for available */}
              {hasSlot && !isPast && !isSelected && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-green-400" />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ScheduleSiteVisit() {
  const { requestId } = useParams()
  const navigate      = useNavigate()
  const profile       = useAuthStore((s) => s.profile)

  const today = new Date()

  const [year,          setYear]          = useState(today.getFullYear())
  const [month,         setMonth]         = useState(today.getMonth())
  const [slots,         setSlots]         = useState([])       // all available slots
  const [selectedDate,  setSelectedDate]  = useState(null)
  const [selectedSlot,  setSelectedSlot]  = useState(null)
  const [loadingSlots,  setLoadingSlots]  = useState(true)
  const [booking,       setBooking]       = useState(false)
  const [done,          setDone]          = useState(false)
  const [confirmedSlot, setConfirmedSlot] = useState(null)
  const [requestValid,  setRequestValid]  = useState(true)

  useEffect(() => {
    async function validate() {
      // Make sure the requestId belongs to this customer
      const { data } = await supabase
        .from('final_proposal_requests')
        .select('id, status, bookings(customer_id)')
        .eq('id', requestId)
        .single()

      if (!data || data.bookings?.customer_id !== profile?.id) {
        setRequestValid(false)
        return
      }
    }
    if (requestId && profile?.id) validate()
  }, [requestId, profile?.id])

  useEffect(() => {
    async function fetchSlots() {
      setLoadingSlots(true)
      const { data } = await supabase
        .from('site_visit_slots')
        .select('id, available_date, available_time, max_visits_this_slot, booked_count')
        .gte('available_date', ymd(today))
        .order('available_date', { ascending: true })
        .order('available_time', { ascending: true })
      // RLS already filters out blocked + full slots
      setSlots(data ?? [])
      setLoadingSlots(false)
    }
    fetchSlots()
  }, []) // eslint-disable-line

  // Dates that have at least one slot
  const availableDates = new Set(slots.map((s) => s.available_date))

  // Slots for selected date
  const slotsForDate = slots.filter((s) => s.available_date === selectedDate)

  function prevMonth() {
    if (month === 0) { setYear((y) => y - 1); setMonth(11) }
    else setMonth((m) => m - 1)
    setSelectedDate(null)
    setSelectedSlot(null)
  }

  function nextMonth() {
    if (month === 11) { setYear((y) => y + 1); setMonth(0) }
    else setMonth((m) => m + 1)
    setSelectedDate(null)
    setSelectedSlot(null)
  }

  function handleSelectDate(d) {
    setSelectedDate(d)
    setSelectedSlot(null)
  }

  async function handleConfirm() {
    if (!selectedSlot) return
    setBooking(true)

    // Insert visit booking
    const { error: insertErr } = await supabase
      .from('site_visit_bookings')
      .insert({ slot_id: selectedSlot.id, request_id: requestId })

    if (insertErr) {
      // Conflict = already booked (UNIQUE constraint)
      setBooking(false)
      return
    }

    // Increment booked_count on slot
    await supabase.rpc('increment_slot_booked_count', { slot_id: selectedSlot.id })
      .then(async () => {}) // best effort — also do it directly
    await supabase
      .from('site_visit_slots')
      .update({ booked_count: selectedSlot.booked_count + 1 })
      .eq('id', selectedSlot.id)

    // Update request status + scheduled_slot_id
    await supabase
      .from('final_proposal_requests')
      .update({
        status:            'site_visit_scheduled',
        scheduled_slot_id: selectedSlot.id,
      })
      .eq('id', requestId)

    setConfirmedSlot(selectedSlot)
    setDone(true)
    setBooking(false)
  }

  // ── Guards ────────────────────────────────────────────────────────────────
  if (!requestValid) {
    return (
      <div className="min-h-dvh bg-surface-base flex items-center justify-center px-4 text-center">
        <div>
          <p className="font-semibold text-ink-primary">Invalid request.</p>
          <button onClick={() => navigate(ROUTES.BOOK_HISTORY)} className="text-sm text-siglat-teal mt-2 hover:underline">
            Back to My Projects
          </button>
        </div>
      </div>
    )
  }

  // ── Success ───────────────────────────────────────────────────────────────
  if (done && confirmedSlot) {
    const dStr = new Date(confirmedSlot.available_date + 'T00:00:00').toLocaleDateString('en-PH', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    })
    return (
      <div className="min-h-dvh bg-surface-base flex flex-col items-center justify-center px-6 text-center gap-5">
        <div className="w-16 h-16 bg-siglat-teal/15 rounded-2xl flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-siglat-teal" />
        </div>
        <div>
          <h2 className="font-display text-xl font-bold text-ink-primary mb-2">
            Site Visit Scheduled
          </h2>
          <p className="text-sm text-ink-muted leading-relaxed">
            Siglat Konstract will visit on
          </p>
          <p className="text-ink-primary font-semibold mt-1">{dStr}</p>
          <p className="text-siglat-teal font-medium text-sm mt-0.5">
            {fmt12(confirmedSlot.available_time)}
          </p>
          <p className="text-xs text-ink-muted mt-3 leading-relaxed">
            You will receive a reminder before your visit.
            After the visit, Siglat Konstract will prepare your final proposal.
          </p>
        </div>
        <Button
          fullWidth
          onClick={() => navigate(ROUTES.BOOK_HISTORY)}
        >
          Back to My Projects
        </Button>
      </div>
    )
  }

  // ── Main ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-dvh bg-surface-base flex flex-col">

      {/* Header */}
      <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-4 safe-top shrink-0">
        <div className="page-container flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-ink-muted hover:text-ink-primary transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-display text-xl font-bold text-ink-primary">Schedule Site Visit</h1>
            <p className="text-xs text-ink-muted">Pick a date and time for Siglat Konstract's inspection</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="page-container py-5 flex flex-col gap-5">

          {loadingSlots ? (
            <div className="h-64 bg-surface-raised rounded-xl animate-pulse border border-surface-border" />
          ) : slots.length === 0 ? (
            <div className="bg-surface-raised border border-surface-border rounded-xl p-10 text-center">
              <CalendarDays className="w-8 h-8 text-ink-muted mx-auto mb-2" />
              <p className="text-sm font-medium text-ink-primary">No available slots yet</p>
              <p className="text-xs text-ink-muted mt-1">
                Siglat Konstract will add available dates soon. Check back shortly.
              </p>
            </div>
          ) : (
            <>
              {/* Legend */}
              <div className="flex items-center gap-4 text-xs text-ink-muted px-1">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
                  Available
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-siglat-teal inline-block" />
                  Selected
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-surface-overlay border border-surface-border inline-block" />
                  Unavailable
                </span>
              </div>

              {/* Calendar */}
              <Calendar
                year={year}
                month={month}
                availableDates={availableDates}
                selectedDate={selectedDate}
                onSelectDate={handleSelectDate}
                onPrevMonth={prevMonth}
                onNextMonth={nextMonth}
              />

              {/* Time slots for selected date */}
              {selectedDate && (
                <div>
                  <p className="text-xs font-semibold text-ink-muted uppercase tracking-widest mb-2 px-1">
                    Available times &mdash; {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-PH', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </p>
                  {slotsForDate.length === 0 ? (
                    <p className="text-sm text-ink-muted px-1">No slots available for this date.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {slotsForDate.map((slot) => (
                        <button
                          key={slot.id}
                          type="button"
                          onClick={() => setSelectedSlot(slot)}
                          className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-colors
                            ${selectedSlot?.id === slot.id
                              ? 'bg-siglat-teal/15 border-siglat-teal text-siglat-teal'
                              : 'bg-surface-overlay border-surface-border text-ink-primary hover:border-siglat-teal/50'
                            }`}
                        >
                          <Clock className="w-3.5 h-3.5 shrink-0" />
                          {fmt12(slot.available_time)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Confirm summary */}
              {selectedSlot && (
                <div className="bg-siglat-teal/10 border border-siglat-teal/30 rounded-xl p-4">
                  <p className="text-sm font-semibold text-ink-primary mb-0.5">Confirm your selection</p>
                  <p className="text-sm text-siglat-teal">
                    {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-PH', {
                      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                    })}
                    {' \u00b7 '}
                    {fmt12(selectedSlot.available_time)}
                  </p>
                  <p className="text-xs text-ink-muted mt-1">
                    A Siglat Konstract representative will visit your property at this time.
                  </p>
                </div>
              )}

            </>
          )}

        </div>
      </div>

      {/* Confirm button */}
      {selectedSlot && (
        <div className="shrink-0 bg-surface-raised border-t border-surface-border px-4 py-4 safe-bottom">
          <div className="page-container">
            <Button
              fullWidth
              size="lg"
              loading={booking}
              onClick={handleConfirm}
            >
              Confirm Site Visit
            </Button>
          </div>
        </div>
      )}

    </div>
  )
}
